import React, { useState, useEffect, useRef } from 'react';
import { useAccount } from 'wagmi';
import {
    TrendingUp,
    TrendingDown,
    Minus,
    Zap,
    Sparkles,
    Loader2
} from 'lucide-react';
import { TokenConfig } from '../../lib/WebAgentEngine';
import { useRecentSignals, useDashboardData } from '../../hooks/useDuckSignals';
import { analyzeSignalWithAI, isAIAvailable, AIAnalysisResult } from '../../services/duckAI';
import { WebAgentEngine } from '../../lib/WebAgentEngine';

interface LiveAnalysisProps {
    token: TokenConfig;
    currentPrice: number;
    priceHistory: number[];
    priceInMon?: number;
    monUsdRate?: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    purpleLight: '#A08BFF',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
    glow: '0 0 40px rgba(255, 215, 0, 0.15)',
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function timeAgo(date: Date): string {
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
    card: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: THEME.glow,
    } as React.CSSProperties,
    header: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(0,0,0,0.2)',
    } as React.CSSProperties,
    body: {
        padding: '1.5rem',
    } as React.CSSProperties,
};

// ═══════════════════════════════════════════════════════════════════════════════
// INDICATOR CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const IndicatorCard: React.FC<{
    label: string;
    value: string | number;
    status: 'bullish' | 'bearish' | 'neutral';
}> = ({ label, value, status }) => {
    const colors = {
        bullish: { bg: `${THEME.green}15`, border: `${THEME.green}40`, color: THEME.green, icon: '📈' },
        bearish: { bg: `${THEME.red}15`, border: `${THEME.red}40`, color: THEME.red, icon: '📉' },
        neutral: { bg: `${THEME.yellow}15`, border: `${THEME.yellow}40`, color: THEME.yellow, icon: '➡️' }
    };
    const { bg, border, color, icon } = colors[status];

    return (
        <div style={{
            padding: '1rem',
            borderRadius: '12px',
            background: bg,
            border: `1px solid ${border}`,
            transition: 'transform 0.2s',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span>{icon}</span>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color }}>{value}</div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const LiveAnalysis: React.FC<LiveAnalysisProps> = ({ token, currentPrice, priceHistory, priceInMon, monUsdRate }) => {
    // Hooks
    useAccount();
    const { signals: recentSignals } = useRecentSignals(5);
    useDashboardData();

    // State
    const [signal, setSignal] = useState<any | null>(null);
    const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
    const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [indicators, setIndicators] = useState({ rsi: 50, macd: 'NEUTRAL', trend: 'NEUTRAL', change: '0%' });
    const [indicatorsReady, setIndicatorsReady] = useState(false);

    // Track last analyzed signal to prevent loops
    const lastAnalyzedRef = useRef<string | null>(null);

    // Engine for analysis
    const engine = React.useMemo(() => new WebAgentEngine(token), [token]);

    // Reset all state when token changes
    const prevTokenRef = useRef<string>(token.address);
    useEffect(() => {
        if (token.address !== prevTokenRef.current) {
            prevTokenRef.current = token.address;
            setSignal(null);
            setAiAnalysis(null);
            setIsAnalyzing(false);
            setIndicators({ rsi: 50, macd: 'NEUTRAL', trend: 'NEUTRAL', change: '0%' });
            setIndicatorsReady(false);
            lastAnalyzedRef.current = null;
            setLastUpdate(new Date());
        }
    }, [token]);

    // Update indicators when price history changes
    useEffect(() => {
        if (priceHistory.length > 0) {
            engine.setPriceHistory(priceHistory);
            const tech = engine.getIndicators();

            const changeVal = priceHistory.length > 1
                ? ((priceHistory[0] - priceHistory[priceHistory.length - 1]) / priceHistory[priceHistory.length - 1] * 100)
                : 0;

            setIndicators({
                rsi: Math.round(tech.rsi),
                macd: tech.macd.histogram > 0.0001 ? 'BULLISH' : tech.macd.histogram < -0.0001 ? 'BEARISH' : 'NEUTRAL',
                trend: tech.momentum > 0.0001 ? 'UP' : tech.momentum < -0.0001 ? 'DOWN' : 'NEUTRAL',
                change: `${changeVal >= 0 ? '+' : ''}${changeVal.toFixed(2)}%`
            });
            setIndicatorsReady(true);
        }
    }, [priceHistory, engine]);

    // Effect to process signals
    useEffect(() => {
        if (recentSignals && recentSignals.length > 0) {
            const latest = recentSignals[0];
            const signalId = `${latest.agent}-${latest.timestamp}-${token.address}`;

            // Convert BigInt to number/string for display
            const formattedSignal = {
                type: latest.signalType,
                confidence: Number(latest.confidence),
                price: Number(latest.price),
                reason: latest.reason,
                timestamp: new Date(Number(latest.timestamp) * 1000),
                agent: latest.agent,
                // Add real indicators to signal for AI
                rsi: indicators.rsi,
                macd: indicators.macd,
                trend: indicators.trend,
                change: indicators.change
            };

            setSignal(formattedSignal);
            setLastUpdate(new Date());

            // Trigger AI analysis ONLY if it's a new signal
            if (formattedSignal && signalId !== lastAnalyzedRef.current) {
                lastAnalyzedRef.current = signalId; // Mark as analyzed
                setIsAnalyzing(true);

                // Call AI
                analyzeSignalWithAI(formattedSignal as any, token)
                    .then(analysis => {
                        setAiAnalysis(analysis);
                    })
                    .catch(e => {
                        console.error('AI Analysis failed:', e);
                        setAiAnalysis({
                            commentary: `AI Analysis unavailable. Market indicators suggest ${indicators.trend} trend with RSI at ${indicators.rsi}.`,
                            educationalInsight: 'Technical indicators (RSI) measure the speed and change of price movements.',
                            riskLevel: indicators.rsi > 70 || indicators.rsi < 30 ? 'HIGH' : 'MEDIUM',
                            keyPoints: ['Technical analysis only', 'Check external sources'],
                            actionSuggestion: `Monitor ${token.symbol} for volatility.`,
                            disclaimer: 'Automated fallback analysis.'
                        });
                    })
                    .finally(() => {
                        setIsAnalyzing(false);
                    });
            }
        }
    }, [recentSignals, token]);

    // Derived State for UI
    const displaySignal = signal || {
        type: 'WAITING',
        confidence: 0,
        price: 0,
        reason: 'Scanning market data...',
        indicators: { rsi: indicators.rsi, macd: indicators.macd, trend: indicators.trend, change: indicators.change }
    };

    const signalColor = displaySignal.type === 'BUY' ? THEME.green :
        displaySignal.type === 'SELL' ? THEME.red : THEME.yellow;

    return (
        <div style={styles.card}>
            {/* Header */}
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ fontSize: '1.5rem' }}>🦆</div>
                    <div>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', margin: 0 }}>Live Signal Analysis</h3>
                        <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>
                            {token?.symbol || 'DUCK'} • <span style={{ color: isAIAvailable() ? THEME.green : THEME.red }}>{isAIAvailable() ? 'AI Active' : 'AI Offline'}</span>
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.5rem 0.75rem', borderRadius: '9999px',
                        background: `${THEME.green}20`, border: `1px solid ${THEME.green}40`,
                    }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: THEME.green, boxShadow: `0 0 10px ${THEME.green}` }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: THEME.green }}>LIVE</span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{timeAgo(lastUpdate)}</span>
                </div>
            </div>

            {/* Body */}
            <div style={styles.body}>
                {/* Market Overview Row */}
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem',
                    background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)'
                }}>
                    <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>PRICE (MON)</div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#fff' }}>
                            {priceInMon && priceInMon > 0
                                ? (priceInMon >= 1 ? priceInMon.toFixed(4) : priceInMon.toPrecision(4))
                                : <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Loading...</span>
                            }
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>PRICE (USD)</div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#fff' }}>
                            {currentPrice > 0
                                ? `$${currentPrice >= 1 ? currentPrice.toFixed(2) : currentPrice.toPrecision(4)}`
                                : <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>—</span>
                            }
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>MON/USD</div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#fff' }}>
                            {monUsdRate && monUsdRate > 0
                                ? `$${monUsdRate.toFixed(2)}`
                                : <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>—</span>
                            }
                        </div>
                    </div>
                </div>

                {/* Signal + Confidence Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    {/* Signal Badge */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '1rem',
                        padding: '1.5rem', borderRadius: '16px',
                        background: `linear-gradient(135deg, ${signalColor}20, ${signalColor}05)`,
                        border: `2px solid ${signalColor}50`,
                        boxShadow: `0 0 30px ${signalColor}20`,
                        transition: 'all 0.3s ease'
                    }}>
                        <span style={{ fontSize: '2rem' }}>
                            {displaySignal.type === 'BUY' ? '🚀' : displaySignal.type === 'SELL' ? '📉' : '⏳'}
                        </span>
                        {displaySignal.type === 'BUY' ? <TrendingUp size={28} color={signalColor} strokeWidth={3} /> :
                            displaySignal.type === 'SELL' ? <TrendingDown size={28} color={signalColor} strokeWidth={3} /> :
                                <Minus size={28} color={signalColor} strokeWidth={3} />}
                        <span style={{ fontSize: '2rem', fontWeight: 800, color: signalColor, letterSpacing: '-0.02em' }}>
                            {displaySignal.type}
                        </span>
                    </div>

                    {/* Confidence Bar */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '0.875rem', color: '#9ca3af', fontWeight: 500 }}>Signal Confidence</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{
                                    fontSize: '2rem',
                                    fontWeight: 800,
                                    color: displaySignal.confidence >= 75 ? THEME.green : displaySignal.confidence >= 50 ? THEME.yellow : THEME.red
                                }}>{displaySignal.confidence}%</span>
                                <span style={{ fontSize: '1.25rem' }}>
                                    {displaySignal.confidence >= 80 ? '🔥' : displaySignal.confidence >= 50 ? '✨' : '⚠️'}
                                </span>
                            </div>
                        </div>
                        <div style={{
                            height: '12px',
                            background: 'rgba(0,0,0,0.4)',
                            borderRadius: '9999px',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                width: `${displaySignal.confidence}%`,
                                height: '100%',
                                borderRadius: '9999px',
                                background: displaySignal.confidence >= 75 ? THEME.green : displaySignal.confidence >= 50 ? THEME.yellow : THEME.red,
                                boxShadow: `0 0 20px ${displaySignal.confidence >= 75 ? THEME.green : displaySignal.confidence >= 50 ? THEME.yellow : THEME.red}80`,
                                transition: 'width 1s ease-out'
                            }} />
                        </div>
                    </div>
                </div>

                {/* Technical Indicators Grid */}
                <div style={{ marginBottom: '1.5rem' }}>
                    {indicatorsReady ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                            <IndicatorCard
                                label="RSI"
                                value={indicators.rsi}
                                status={indicators.rsi > 70 ? "bearish" : indicators.rsi < 30 ? "bullish" : "neutral"}
                            />
                            <IndicatorCard
                                label="MACD"
                                value={indicators.macd}
                                status={indicators.macd === "BULLISH" ? "bullish" : indicators.macd === "BEARISH" ? "bearish" : "neutral"}
                            />
                            <IndicatorCard
                                label="Trend"
                                value={indicators.trend}
                                status={indicators.trend === "UP" ? "bullish" : indicators.trend === "DOWN" ? "bearish" : "neutral"}
                            />
                            <IndicatorCard
                                label="Changes"
                                value={indicators.change}
                                status={indicators.change.startsWith("-") ? "bearish" : indicators.change === "+0.00%" || indicators.change === "0%" ? "neutral" : "bullish"}
                            />
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                            {['RSI', 'MACD', 'Trend', 'Changes'].map(label => (
                                <div key={label} style={{
                                    padding: '1rem', borderRadius: '12px',
                                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                                }}>
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.5rem' }}>{label}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280' }}>
                                        <Loader2 size={14} className="animate-spin" />
                                        <span style={{ fontSize: '0.85rem' }}>Loading...</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* AI Analysis Section */}
                <div style={{
                    padding: '1.25rem',
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.05)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div style={{
                            padding: '0.4rem',
                            background: 'linear-gradient(135deg, #836EF9 0%, #5E17EB 100%)',
                            borderRadius: '8px'
                        }}>
                            <Sparkles size={18} color="#fff" />
                        </div>
                        <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>AI Agent Reasoning</span>
                    </div>

                    {isAnalyzing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                            <Loader2 size={16} className="animate-spin" />
                            <span>Analyzing market conditions...</span>
                        </div>
                    ) : aiAnalysis ? (
                        <div>
                            <p style={{ color: '#d1d5db', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                                {aiAnalysis.commentary}
                            </p>

                            {/* Educational Insight */}
                            {aiAnalysis.educationalInsight && (
                                <div style={{
                                    marginBottom: '1rem',
                                    padding: '0.75rem',
                                    background: 'rgba(131, 110, 249, 0.1)',
                                    borderRadius: '8px',
                                    borderLeft: `3px solid ${THEME.purple}`
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                        <Zap size={14} color={THEME.purpleLight} />
                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: THEME.purpleLight, textTransform: 'uppercase' }}>Educational Insight</span>
                                    </div>
                                    <p style={{ fontSize: '0.85rem', color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>
                                        "{aiAnalysis.educationalInsight}"
                                    </p>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <div style={{
                                    padding: '0.25rem 0.75rem',
                                    background: 'rgba(255,255,255,0.05)',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    color: '#9ca3af',
                                    border: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    Risk Level: <span style={{ color: aiAnalysis.riskLevel === 'LOW' ? THEME.green : aiAnalysis.riskLevel === 'MEDIUM' ? THEME.yellow : THEME.red, fontWeight: 700 }}>{aiAnalysis.riskLevel}</span>
                                </div>
                                <div style={{
                                    padding: '0.25rem 0.75rem',
                                    background: 'rgba(255,255,255,0.05)',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    color: '#9ca3af',
                                    border: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    Action: <span style={{ color: '#fff', fontWeight: 600 }}>{aiAnalysis.actionSuggestion}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', fontStyle: 'italic' }}>
                            Waiting for market signals to generate analysis...
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LiveAnalysis;
